import { useState, useEffect, useCallback } from 'react';
import { useFetcher, useLocation, useNavigate } from '@remix-run/react';
import { selectionStore } from '~/lib/persistence/selectionStore';
import { db, projectId } from '~/lib/persistence/useChatHistory';
import { getMessagesById, updateNetlifyUrl } from '~/lib/persistence/db';
import { initiateOAuth, resetOAuthState } from '~/lib/utils/netlify-helpers';
import type { UseNetlifyAuthReturn, NetlifyUser, ChatDeployment } from '~/lib/types/netlify';
import { useInterval } from './useInterval';
import { setHasExistingDeployment, setError } from '~/lib/stores/netlify';
import * as Sentry from '@sentry/remix';
import type { ConnectionData } from '~/services/interfaceService';
import axios from 'axios';
import { toast } from 'react-toastify';

const validateNetlifyToken = async (token: string): Promise<boolean> => {
  console.log('[validateNetlifyToken] Validating token...');

  try {
    const response = await fetch('https://api.netlify.com/api/v1/user', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    const data = await response.json();

    console.log('[int] validating netlify token: ', data);

    return response.ok;
  } catch (error) {
    return false;
  }
};

export function useNetlifyAuth(
  netlifyConfig: {
    NEXT_PUBLIC_NETLIFY_CLIENT_ID: string;
    NEXT_PUBLIC_NETLIFY_APP_URL: string;
  },
  cleanupLogs?: () => void,
): UseNetlifyAuthReturn {
  const navigate = useNavigate();
  const location = useLocation();
  const fetcher = useFetcher<{ tokens: Record<string, any> }>();

  // State management
  const [accessToken, setAccessToken] = useState<string | null>();
  const [user, setUser] = useState<NetlifyUser | null>(null);
  const [isValidatingToken, setIsValidatingToken] = useState(false);
  const [isCheckingToken, setIsCheckingToken] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [tokenCheckCount, setTokenCheckCount] = useState(0);

  // Fetch user details
  const fetchUserDetails = useCallback(async () => {
    if (!accessToken) {
      return;
    }

    try {
      const response = await fetch('https://api.netlify.com/api/v1/user', {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        await handleDisconnect();
        return;
      }

      const userData = await response.json();
      setUser(userData as NetlifyUser);
    } catch (err) {
      console.error('Error fetching user details:', err);
      await handleDisconnect();
      Sentry.captureException(err, {
        extra: {
          message: 'Error fetching Netlify user details',
          accessToken: accessToken ? 'present' : 'missing',
        },
      });
    }
  }, [accessToken]);

  useEffect(() => {
    if (fetcher.state === 'idle' && !fetcher.data) {
      const storedToken = selectionStore.get().netlifyAccessToken || '';
      fetcher.submit(
        { provider: 'netlify', localAccessToken: storedToken },
        { method: 'POST', action: '/api/access-token', encType: 'application/json' },
      );
    }
  }, [fetcher]);

  // Check for existing token in chat or storage
  useEffect(() => {
    if (fetcher.data?.tokens) {
      const { netlify } = fetcher.data.tokens;
      console.log('[int] fetching token for netlify: ', netlify);

      if (netlify) {
        selectionStore.setKey('netlifyAccessToken', netlify.accessToken);
      }
    }

    const getAccessToken = async () => {
      // Early return if no db or chatId or projectId
      if (!db || !projectId.get()) {
        return await handleStoredTokenValidation();
      }

      const project = await getMessagesById(db, projectId.get()!);

      // TODO: https://dualite.sentry.io/issues/7110210520/events/7640925fb56841daa6fbe8b818cb730c/
      const projectData = 'netlifyCreds' in project ? (project as ChatDeployment) : null;
      const projectToken = projectData?.netlifyCreds?.netlifyToken;
      const storedToken = selectionStore.get().netlifyAccessToken;

      // If no chat token, validate stored token
      if (!projectToken) {
        return await handleStoredTokenValidation();
      }

      // Validate chat token
      const isProjectTokenValid = await validateNetlifyToken(projectToken);

      if (isProjectTokenValid) {
        setAccessToken(projectToken);
        selectionStore.setKey('netlifyAccessToken', projectToken);
        return true;
      }

      // Chat token invalid, try stored token
      const isStoredTokenValid = storedToken && (await validateNetlifyToken(storedToken));

      if (isStoredTokenValid) {
        setAccessToken(storedToken);
        await updateNetlifyCreds(db, projectId.get()!, projectData, storedToken);
        return true;
      }

      // Both tokens invalid, clear credentials
      await updateNetlifyCreds(db, projectId.get()!, projectData, '');
      await handleDisconnect();
      return false;
    };

    const handleStoredTokenValidation = async () => {
      const storedToken = selectionStore.get().netlifyAccessToken;
      const isValid = await validateNetlifyToken(storedToken);

      if (isValid) {
        setAccessToken(storedToken);
        return true;
      }

      // await handleDisconnect();
      return false;
    };

    const updateNetlifyCreds = async (
      db: IDBDatabase,
      projectId: string,
      chatData: ChatDeployment | null,
      token: string,
    ) => {
      const connectionData: ConnectionData['connectionData'] = {
        netlifyToken: token,
        netlifyUrl: chatData?.netlifyCreds?.netlifyUrl || '',
        netlifySiteId: chatData?.netlifyCreds?.netlifySiteId || '',
        inspectDeployment: chatData?.netlifyCreds?.inspectDeployment || '',
      };

      // IDB upload
      await updateNetlifyUrl(db, projectId, connectionData);

      // cloudRevert (burst)
      // cloud upload
      // await updateProjectConnection(projectId, {
      //   connectionType: 'netlify',
      //   connectionData,
      // });
    };

    getAccessToken();
  }, [fetcher.data]);

  // Effect to fetch user details when accessToken changes
  useEffect(() => {
    if (accessToken) {
      fetchUserDetails();
    }
  }, [accessToken, fetchUserDetails]);

  // Validate token function
  const validateToken = useCallback(async (): Promise<boolean> => {
    if (!accessToken) {
      return false;
    }

    setIsValidatingToken(true);

    const isValid = await validateNetlifyToken(accessToken);
    setIsValidatingToken(false);

    if (!isValid) {
      await handleDisconnect();
    }

    return isValid;
  }, [accessToken]);

  // Handle disconnect
  const handleDisconnect = useCallback(async () => {
    try {
      setIsDisconnecting(true);

      await axios.delete('/api/user-integration', {
        data: {
          provider: 'netlify',
        },
      });

      console.log('[useNetlifyAuth] Disconnecting Netlify account');
      selectionStore.setKey('netlifyAccessToken', '');
      selectionStore.setKey('netlifySiteId', '');
      setAccessToken(null);
      setUser(null);

      // Reset deployment state
      setHasExistingDeployment(false);
      setError('');

      // Clean up logs when disconnecting Netlify account
      if (cleanupLogs) {
        cleanupLogs();
      }
    } catch (error) {
      console.error('Error deleting user integration from SupabaseOAuthModal:', error);
      Sentry.captureException(error);
      toast.error('Failed to disconnect Netlify. Please try again or refresh the page.');
    } finally {
      setIsDisconnecting(false);
    }
  }, [cleanupLogs]);

  // Initiate OAuth flow
  const initiateOAuthFlow = useCallback(() => {
    setTokenCheckCount(0);
    setIsCheckingToken(true);
    initiateOAuth(netlifyConfig, setIsCheckingToken, setTokenCheckCount);
  }, [netlifyConfig]);

  // Reset auth state
  const resetAuthState = useCallback(() => {
    resetOAuthState(setIsCheckingToken, setTokenCheckCount);
  }, []);

  // Interval to check for token availability during OAuth flow
  useInterval(
    () => {
      const storedToken = selectionStore.get().netlifyAccessToken;

      if (storedToken) {
        setAccessToken(storedToken);
        setIsCheckingToken(false);
        setTokenCheckCount(0);
        return;
      }

      setTokenCheckCount((prev) => {
        const newCount = prev + 1;

        if (newCount >= 30) {
          setIsCheckingToken(false);
          return 0;
        }

        return newCount;
      });
    },
    isCheckingToken ? 1000 : null,
  );

  // Handle OAuth callback
  useEffect(() => {
    // Handle OAuth callback
    const params = new URLSearchParams(location.search);
    const code = params.get('code');
    const state = params.get('state');
    const savedState = localStorage.getItem('netlifyState');

    if (state === savedState) {
      fetch('/api/netlify-oauth/callback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code }),
      })
        .then((res) => res.json())
        .then((data: unknown) => {
          const tokenData = data as { access_token?: string };

          if (tokenData.access_token) {
            setAccessToken(tokenData.access_token);
            selectionStore.setKey('netlifyAccessToken', tokenData.access_token);
            localStorage.removeItem('netlifyState');
            navigate(location.pathname, { replace: true });
          }
        });
    }
  }, [location, navigate]);

  return {
    accessToken,
    user,
    isValidatingToken,
    isCheckingToken,
    tokenCheckCount,
    isDisconnecting,
    validateToken,
    fetchUserDetails,
    handleDisconnect,
    initiateOAuthFlow,
    resetAuthState,
  };
}
